/**
 * Unified Agent Definition page (DR1) — Basic Information + Definition Blueprint
 * on one scrollable surface. Save persists State (no separate Publish CTA).
 * Private / Public / Archived agents are read-only except State.
 *
 * Layout: Draft agents — authoring (left) + definition form with identity header (right).
 * Released agents — full-width form only.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import {
  ASSIST_FORM_BODY_CLASS,
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_SHELL_CLASS,
  ASSIST_PANE_TITLE_CLASS,
  ASSIST_SPLIT_GRID_CLASS,
  ASSIST_WORKSPACE_CONTAINER_CLASS,
  ASSIST_WORKSPACE_PAGE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import {
  AgentToolsEditor,
  ComputeSlotsEditor,
  ExpectedRowsEditor,
  FIELD_CLASS,
  FieldLabel,
  GovernanceNeverFieldset,
  HINT_CLASS,
  LABEL_CLASS,
  SECTION_CLASS,
  useAgentDefinition,
  AgentSetupPanel,
  AgentAuthoringInterview,
} from '@/components/ai-studio/definition';
import {
  ARCHETYPE_CLASS_MAP,
  STUDIO_AGENT_ARCHETYPES,
  STUDIO_AGENT_DEFINITION_STATES,
  STUDIO_AGENT_LOCKED_STATES,
  STUDIO_COMPUTE_CLASSES,
  STUDIO_DECISION_MODES,
} from '@/types/aiStudioAgent';
import type {
  StudioAgentArchetype,
  StudioComputeClass,
  StudioDecisionMode,
} from '@/types/aiStudioAgent';
import { startStudioTestRun } from '@/services/aiStudioAgentService';

const AIStudioAgentDefinitionPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin =
    user?.role === 'Platform Administrator' || user?.role === 'PLATFORM_ADMIN';
  const { agentId } = useParams<{ agentId: string }>();
  const editor = useAgentDefinition(agentId);
  const [mobilePane, setMobilePane] = useState<'chat' | 'blueprint'>('chat');
  const [testing, setTesting] = useState(false);
  const [testErrorKey, setTestErrorKey] = useState<string | null>(null);

  const { agent, version, agentDraft, versionDraft, agentReadOnly, blueprintReadOnly } =
    editor;

  // REQ-AIS-117: tenants may view catalog agents; only Platform Administrators edit definitions.
  const definitionLocked = !isPlatformAdmin;
  const formAgentReadOnly = agentReadOnly || definitionLocked;
  const formBlueprintReadOnly = blueprintReadOnly || definitionLocked;

  const stateOptions = useMemo(() => {
    if (!agentDraft) return STUDIO_AGENT_DEFINITION_STATES;
    // Once released, State only cycles among private / public / archived.
    if (agentDraft.visibility !== 'draft') {
      return STUDIO_AGENT_LOCKED_STATES;
    }
    return STUDIO_AGENT_DEFINITION_STATES;
  }, [agentDraft]);

  const selectablePacks = useMemo(() => {
    if (!versionDraft) return [];
    return editor.packs.filter(
      (pack) =>
        pack.lifecycleStatus === 'published' || pack.id === versionDraft.industryPackId
    );
  }, [editor.packs, versionDraft]);

  const availableArchetypes = useMemo(() => {
    if (!versionDraft) return [];
    return STUDIO_AGENT_ARCHETYPES.filter((a) =>
      versionDraft.computeClasses.includes(ARCHETYPE_CLASS_MAP[a])
    );
  }, [versionDraft]);

  const toggleComputeClass = (computeClass: StudioComputeClass, enabled: boolean) => {
    if (!versionDraft || formBlueprintReadOnly) return;
    const next = STUDIO_COMPUTE_CLASSES.filter((candidate) =>
      candidate === computeClass ? enabled : versionDraft.computeClasses.includes(candidate)
    );
    if (next.length === 0) return;
    const currentArchetype = versionDraft.archetype as StudioAgentArchetype | null;
    const archetypeClassRemoved =
      currentArchetype &&
      ARCHETYPE_CLASS_MAP[currentArchetype] === computeClass &&
      !enabled;
    editor.applyVersionPatch({
      computeClasses: next,
      ...(archetypeClassRemoved ? { archetype: null } : {}),
    });
  };

  const handleTestDraft = async () => {
    if (!isPlatformAdmin || !agent || !version || version.lifecycleStatus !== 'draft' || testing) {
      return;
    }
    setTesting(true);
    setTestErrorKey(null);
    try {
      // Always persist the working copy so Test runs the blueprint currently on screen.
      const saved = await editor.save();
      if (!saved) return;
      const started = await startStudioTestRun(agent.id);
      const params = new URLSearchParams();
      if (started.conversationId) params.set('conversationId', started.conversationId);
      params.set('instanceId', started.instanceId);
      navigate(
        `/ai-studio/runs/${encodeURIComponent(started.run.id)}?${params.toString()}`
      );
    } catch {
      setTestErrorKey('aiStudio.definition.testFailed');
    } finally {
      setTesting(false);
    }
  };

  if (editor.loading) {
    return (
      <main className="px-4 py-4 md:px-6 lg:px-8">
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.definition.loading')}
        </p>
      </main>
    );
  }

  if (!agent || !version || !agentDraft || !versionDraft) {
    return (
      <main className="flex flex-col gap-3 px-4 py-4 md:px-6 lg:px-8">
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(editor.errorKey ?? 'aiStudio.agents.notFound')}
        </p>
        <Link to="/ai-studio/agents" className="w-fit text-base text-[var(--color-info)] underline">
          {t('aiStudio.definition.backToAgents')}
        </Link>
      </main>
    );
  }

  const showAuthoring =
    isPlatformAdmin && !formBlueprintReadOnly && agentDraft?.visibility === 'draft';

  const canFactoryTest = isPlatformAdmin && version.lifecycleStatus === 'draft';
  /** Test + Save share the same header action cluster (size/chrome). */
  const blueprintActions = (size: 'sm' | 'md' = 'sm') => (
    <div className="flex shrink-0 items-center gap-2">
      {canFactoryTest ? (
        <Button
          type="button"
          variant="success"
          size={size}
          onClick={() => void handleTestDraft()}
          disabled={testing || editor.saving || !editor.jsonValid}
          loading={testing}
        >
          {testing ? t('aiStudio.definition.testing') : t('aiStudio.definition.test')}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="success"
        size={size}
        onClick={() => void editor.save()}
        disabled={editor.saving || testing || !editor.dirty || !editor.jsonValid}
        loading={editor.saving && !testing}
      >
        {editor.saving && !testing ? t('aiStudio.common.saving') : t('aiStudio.common.save')}
      </Button>
    </div>
  );

  const identityHeader = (
    <header className="flex flex-col gap-1">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-[2]">
          <label className={LABEL_CLASS} htmlFor="def-agent-name">
            {t('aiStudio.agents.form.name')}
          </label>
          <input
            id="def-agent-name"
            className={`${FIELD_CLASS} font-semibold`}
            value={agentDraft.name}
            placeholder={t('aiStudio.agents.form.name')}
            disabled={formAgentReadOnly}
            onChange={(e) => editor.setAgentField('name', e.target.value)}
          />
        </div>
        <div className="min-w-[8rem] flex-1">
          <label className={LABEL_CLASS} htmlFor="def-agent-domain">
            {t('aiStudio.agents.form.domain')}
          </label>
          <input
            id="def-agent-domain"
            className={FIELD_CLASS}
            value={agentDraft.domain}
            disabled={formAgentReadOnly}
            onChange={(e) => editor.setAgentField('domain', e.target.value)}
          />
        </div>
        <div className="min-w-[8rem] w-40">
          <label className={LABEL_CLASS} htmlFor="def-agent-visibility">
            {t('aiStudio.definition.state')}
          </label>
          <select
            id="def-agent-visibility"
            className={FIELD_CLASS}
            value={agentDraft.visibility}
            onChange={(e) =>
              editor.setAgentField('visibility', e.target.value as typeof agentDraft.visibility)
            }
          >
            {stateOptions.map((vis) => (
              <option key={vis} value={vis}>
                {t(`aiStudio.definition.visibility.${vis}`)}
              </option>
            ))}
          </select>
        </div>
        {!showAuthoring && <div className="pb-0.5">{blueprintActions('md')}</div>}
      </div>
      <p
        className={`${HINT_CLASS} font-mono`}
        title={t('aiStudio.definition.agentCodeTooltip')}
      >
        {t('aiStudio.definition.agentCodeLabel')}: {agentDraft.code}
        {!editor.codeLocked && (
          <span className="ml-2 font-sans text-[var(--color-surface-text-secondary)]">
            ({t('aiStudio.definition.agentCodeTracksName')})
          </span>
        )}
      </p>
      {formAgentReadOnly && <p className={HINT_CLASS}>{t('aiStudio.definition.readOnlyHint')}</p>}
    </header>
  );

  const definitionSections = (
    <>
          {editor.errorKey && (
            <div
              role="alert"
              className="whitespace-pre-line rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
            >
              {editor.errorDetail ?? t(editor.errorKey)}
            </div>
          )}
          {testErrorKey && (
            <div
              role="alert"
              className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
            >
              {t(testErrorKey)}
            </div>
          )}

      {/* About — row 1: Role | Objective | Success; row 2: Context | Constraints | Rules */}
      <section className={SECTION_CLASS}>
        <FieldLabel as="span" tooltip={t('aiStudio.definition.personaSubtitle')} className="font-semibold">
          {t('aiStudio.definition.personaTitle')}
        </FieldLabel>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="min-w-0">
            <FieldLabel htmlFor="def-agent-role" tooltip={t('aiStudio.definition.rolePlaceholder')}>
              {t('aiStudio.definition.role')}
            </FieldLabel>
            <textarea
              id="def-agent-role"
              className={FIELD_CLASS}
              rows={5}
              value={agentDraft.expertPosture}
              disabled={formAgentReadOnly}
              placeholder={t('aiStudio.definition.rolePlaceholder')}
              onChange={(e) => editor.setAgentField('expertPosture', e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <FieldLabel
              htmlFor="def-agent-objective"
              tooltip={t('aiStudio.definition.objectivePlaceholder')}
            >
              {t('aiStudio.definition.objective')}
            </FieldLabel>
            <textarea
              id="def-agent-objective"
              className={FIELD_CLASS}
              rows={5}
              value={agentDraft.purpose}
              disabled={formAgentReadOnly}
              placeholder={t('aiStudio.definition.objectivePlaceholder')}
              onChange={(e) => editor.setAgentField('purpose', e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <FieldLabel htmlFor="def-success" tooltip={t('aiStudio.definition.successCriteriaHint')}>
              {t('aiStudio.definition.successCriteria')}
            </FieldLabel>
            <textarea
              id="def-success"
              className={FIELD_CLASS}
              rows={5}
              value={versionDraft.successCriteria}
              disabled={formBlueprintReadOnly}
              placeholder={t('aiStudio.definition.successCriteriaPlaceholder')}
              onChange={(e) => editor.setVersionField('successCriteria', e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="min-w-0">
            <FieldLabel
              htmlFor="def-context"
              tooltip={t('aiStudio.definition.contextDefinitionHint')}
            >
              {t('aiStudio.definition.contextDefinition')}
            </FieldLabel>
            <textarea
              id="def-context"
              className={FIELD_CLASS}
              rows={6}
              value={versionDraft.contextDefinition}
              disabled={formBlueprintReadOnly}
              onChange={(e) => editor.setVersionField('contextDefinition', e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <FieldLabel
              htmlFor="def-agent-constraints"
              tooltip={t('aiStudio.definition.constraintsHint')}
            >
              {t('aiStudio.definition.constraints')}
            </FieldLabel>
            <textarea
              id="def-agent-constraints"
              className={FIELD_CLASS}
              rows={6}
              value={agentDraft.scopeHonesty}
              disabled={formAgentReadOnly}
              placeholder={t('aiStudio.definition.constraintsPlaceholder')}
              onChange={(e) => editor.setAgentField('scopeHonesty', e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <FieldLabel as="span" tooltip={t('aiStudio.definition.sectionRulesHint')}>
              {t('aiStudio.definition.sectionRules')}
            </FieldLabel>
            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <FieldLabel
                  htmlFor="def-default-decision-mode"
                  tooltip={t('aiStudio.definition.defaultDecisionModeHint')}
                >
                  {t('aiStudio.definition.defaultDecisionMode')}
                </FieldLabel>
                <select
                  id="def-default-decision-mode"
                  className={FIELD_CLASS}
                  value={versionDraft.defaultDecisionMode}
                  disabled={formBlueprintReadOnly}
                  onChange={(e) =>
                    editor.setVersionField(
                      'defaultDecisionMode',
                      e.target.value as StudioDecisionMode
                    )
                  }
                >
                  {STUDIO_DECISION_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(`aiStudio.definition.defaultDecisionMode.${mode}`)}
                    </option>
                  ))}
                </select>
              </div>
              <GovernanceNeverFieldset
                value={versionDraft.autonomyFences}
                disabled={formBlueprintReadOnly}
                onChange={(next) => editor.applyVersionPatch({ autonomyFences: next })}
              />
            </div>
          </div>
        </div>
      </section>

      <FieldLabel as="span" tooltip={t('aiStudio.definition.howItWorksHint')} className="pt-2 font-semibold">
        {t('aiStudio.definition.howItWorks')}
      </FieldLabel>

      {/* Inputs | Outputs | Compute — three blocks; I/O rows are collapsible */}
      <div className="grid gap-4 lg:grid-cols-3">
        <section className={`${SECTION_CLASS} min-w-0`}>
          <ExpectedRowsEditor
            label={t('aiStudio.definition.inputExpected')}
            hint={t('aiStudio.definition.inputExpectedHint')}
            kind="input"
            rows={versionDraft.inputExpected}
            disabled={formBlueprintReadOnly}
            onChange={(rows) => editor.setVersionField('inputExpected', rows)}
          />
        </section>
        <section className={`${SECTION_CLASS} min-w-0`}>
          <ExpectedRowsEditor
            label={t('aiStudio.definition.outputExpected')}
            hint={t('aiStudio.definition.outputExpectedHint')}
            kind="output"
            rows={versionDraft.outputExpected}
            disabled={formBlueprintReadOnly}
            onChange={(rows) => editor.setVersionField('outputExpected', rows)}
          />
        </section>
        <section className={`${SECTION_CLASS} min-w-0`}>
          <FieldLabel as="span" className="font-semibold">
            {t('aiStudio.definition.sectionCompute')}
          </FieldLabel>
          <fieldset className="mb-3">
            <legend className={LABEL_CLASS}>{t('aiStudio.versions.computeClasses')}</legend>
            <div className="flex flex-col gap-2">
              {STUDIO_COMPUTE_CLASSES.map((cls) => (
                <label
                  key={cls}
                  className="flex items-center gap-2 text-base text-[var(--color-surface-text)]"
                >
                  <input
                    type="checkbox"
                    checked={versionDraft.computeClasses.includes(cls)}
                    disabled={formBlueprintReadOnly}
                    onChange={(e) => toggleComputeClass(cls, e.target.checked)}
                  />
                  {t(`aiStudio.versions.computeClass.${cls}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="mb-3 flex flex-col gap-3">
            <div>
              <label className={LABEL_CLASS} htmlFor="def-archetype">
                {t('aiStudio.versions.archetype')}
              </label>
              <select
                id="def-archetype"
                className={FIELD_CLASS}
                value={versionDraft.archetype ?? ''}
                disabled={formBlueprintReadOnly || availableArchetypes.length === 0}
                onChange={(e) =>
                  editor.applyVersionPatch({ archetype: e.target.value || null })
                }
              >
                <option value="">{t('aiStudio.common.none')}</option>
                {availableArchetypes.map((a) => (
                  <option key={a} value={a}>
                    {t(`aiStudio.versions.archetype.${a}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS} htmlFor="def-pack">
                {t('aiStudio.versions.industryPack')}
              </label>
              <select
                id="def-pack"
                className={FIELD_CLASS}
                value={versionDraft.industryPackId ?? ''}
                disabled={formBlueprintReadOnly}
                onChange={(e) =>
                  editor.applyVersionPatch({ industryPackId: e.target.value || null })
                }
              >
                <option value="">{t('aiStudio.common.none')}</option>
                {selectablePacks.map((pack) => (
                  <option key={pack.id} value={pack.id}>
                    {pack.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ComputeSlotsEditor versionId={version.id} disabled={formBlueprintReadOnly} />
          <div className="mt-4">
            <AgentToolsEditor versionId={version.id} disabled={formBlueprintReadOnly} />
          </div>
          <div className="mt-3">
            <FieldLabel
              htmlFor="def-instructions"
              tooltip={t('aiStudio.definition.explainInstructionsHint')}
            >
              {t('aiStudio.definition.explainInstructions')}
            </FieldLabel>
            <textarea
              id="def-instructions"
              className={FIELD_CLASS}
              rows={4}
              value={versionDraft.explainInstructions}
              disabled={formBlueprintReadOnly}
              onChange={(e) => editor.setVersionField('explainInstructions', e.target.value)}
            />
          </div>
        </section>

        <section className={SECTION_CLASS} aria-labelledby="agent-setup-heading" id="agent-setup">
          <h2 id="agent-setup-heading" className="mb-2 text-lg font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.setup.sectionTitle')}
          </h2>
          <AgentSetupPanel
            agentId={agent.id}
            agentCode={agentDraft.code}
            versionId={version.id}
            readOnly={false}
          />
        </section>
      </div>

      <footer className="border-t border-[var(--color-border)] pt-4">
        <Link
          to={`/ai-studio/agents/${agent.id}/versions/${version.id}`}
          className="text-base text-[var(--color-info)] underline"
        >
          {t('aiStudio.definition.advancedVersionEditor')}
        </Link>
      </footer>
    </>
  );

  if (showAuthoring) {
    return (
      <div className={ASSIST_WORKSPACE_PAGE_CLASS}>
        <div className={ASSIST_WORKSPACE_CONTAINER_CLASS}>
          <div className="mb-4 shrink-0">
            <nav className="mb-2 flex flex-wrap items-center gap-3 text-sm">
              <Link
                to="/ai-studio/agents"
                className="text-[var(--color-info)] underline"
              >
                {t('aiStudio.definition.backToAgents')}
              </Link>
              <Link
                to={`/ai-studio/agents/${agent.id}/versions`}
                className="text-[var(--color-info)] underline"
              >
                {t('aiStudio.definition.versionHistory')}
              </Link>
            </nav>
            <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
              {t('aiStudio.definition.title', { name: agentDraft.name })}
            </h1>
            <p className="text-sm text-[var(--color-secondary-text)]">
              {t('aiStudio.definition.pageSubtitle')}
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-2 flex flex-shrink-0 items-center justify-end gap-2 px-1 lg:hidden">
              {mobilePane === 'blueprint' ? (
                <button
                  type="button"
                  onClick={() => setMobilePane('chat')}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
                >
                  {t('aiStudio.definition.backToChat')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setMobilePane('blueprint')}
                  className="rounded-lg bg-[var(--color-primary-bg)] px-4 py-2.5 text-base font-medium text-[var(--color-primary-text)] shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  {t('aiStudio.definition.blueprintPane')}
                </button>
              )}
            </div>

            <div className={ASSIST_SPLIT_GRID_CLASS}>
              <div
                className={`${ASSIST_PANE_SHELL_CLASS} ${
                  mobilePane === 'chat' ? 'flex' : 'hidden'
                } lg:flex lg:h-full`}
              >
                <AgentAuthoringInterview
                  agentId={agent.id}
                  versionId={version.id}
                  agentName={agentDraft.name}
                  enabled={!editor.saving}
                  workspacePane
                  onApplyAgentPatch={editor.applyAgentPatch}
                  onApplyVersionPatch={editor.applyVersionPatch}
                />
              </div>

              <div
                className={`${ASSIST_PANE_SHELL_CLASS} ${
                  mobilePane === 'blueprint' ? 'flex' : 'hidden'
                } lg:flex lg:h-full`}
              >
                <div className={ASSIST_PANE_HEADER_CLASS}>
                  <h2 className={ASSIST_PANE_TITLE_CLASS}>{t('aiStudio.definition.blueprintPane')}</h2>
                  {blueprintActions('sm')}
                </div>
                <div className={ASSIST_FORM_BODY_CLASS}>
                  {identityHeader}
                  {definitionSections}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex w-full flex-col gap-3 px-4 py-3 md:px-6 lg:px-8">
      <nav className="flex flex-shrink-0 flex-wrap items-center gap-3">
        <Link to="/ai-studio/agents" className="text-base text-[var(--color-info)] underline">
          {t('aiStudio.definition.backToAgents')}
        </Link>
        <Link
          to={`/ai-studio/agents/${agent.id}/versions`}
          className="text-base text-[var(--color-info)] underline"
        >
          {t('aiStudio.definition.versionHistory')}
        </Link>
      </nav>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {identityHeader}
        {definitionSections}
      </div>
    </main>
  );
};

export default AIStudioAgentDefinitionPage;
